import { useQuery } from '@tanstack/react-query'
import classnames from 'classnames/bind'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { TRACKING_EVENTS } from '~/lib/constants'
import { getJudgemeReviews } from '~/lib/handle-api'

import { LinkProps } from '@unlikelystudio/react-abstract-components'

import BlurredCta from '~/components/UI/BlurredCta'
import Image, { ImageProps } from '~/components/UI/Image'
import ProductReview, {
  Review,
} from '~/components/UI/ProductReviewsList/ProductReview'
import StarRatings from '~/components/UI/StarRatings'

import { useStyle } from '~/providers/StyleProvider'
import { useTracker } from '~/providers/TrackerProvider'

import useAlerts from '~/hooks/useAlerts'
import useLocale from '~/hooks/useLocale'
import useTranslatedSmartCount from '~/hooks/useTranslatedSmartCount'

import numberAsBoolean from '~/utils/number-as-boolean'

import { CTA, REVIEWS } from '~/data/dictionary'
import { serializeReview } from '~/data/serialize-review'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface ProductReviewsListProps {
  className?: string
  apiPage?: number
  pagesTotal?: number
  judgeMeID?: string
  reviews?: Review[]
  productReviewsCount?: number
  innerRef?: any
  header?: ProductReviewsListHeaderProps
  productAverageRatings?: number
}

export interface ProductReviewsListHeaderProps {
  title?: string
  image?: ImageProps
  link?: LinkProps
}

const IMAGE_SIZES = [{ ratio: 3 / 12 }]

function ProductReviewsList({
  className,
  apiPage,
  pagesTotal,
  judgeMeID,
  reviews,
  innerRef,
  header,
  productAverageRatings,
  productReviewsCount,
}: ProductReviewsListProps) {
  const processedPage = useRef(apiPage)
  const locale = useLocale()
  const [results, setResults] = useState([])
  const [page] = useState(apiPage)
  const triggerAlert = useAlerts()
  const tracker = useTracker()
  const { data, isLoading, refetch } = useQuery(
    ['getJudgemeReviews'],
    async () => {
      try {
        if (page >= pagesTotal) return
        const response = await getJudgemeReviews({
          locale,
          page: String(processedPage?.current ?? null),
          totalPages: String(pagesTotal ?? 0),
          productId: judgeMeID,
        })

        if (response?.errors) {
          return triggerAlert()
        }
        const [reviews, apiPage] = response?.body ?? {}
        return [
          reviews?.map((review) => serializeReview(review, locale)),
          apiPage,
        ]
      } catch (error) {
        console.log(error)
      }
    },
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onError: (err) => {
        console.log('error', err)
      },
    },
  )

  useEffect(() => {
    if (!data) return
    setResults((results) => [...results, ...(data?.[0] ?? [])])
    processedPage.current = data?.[1] ?? apiPage
  }, [data])

  const loadNextPage = () => {
    refetch()
  }

  const processedResults = useMemo(() => {
    if (!reviews) return null
    return [...reviews, ...(results ?? [])]?.filter(
      // make sure we don't have a duplicate review showing because of the partial page fetch
      (review, key, self) =>
        self.findIndex((r) => r?.id === review?.id) === key,
    )
  }, [results, reviews])

  const t = useTranslate()
  const triggerEvent = () => tracker.emit(TRACKING_EVENTS.LEAVE_REVIEW_CTA)

  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Title16Primary,
    textStyling: GlobalTextStyling.UpperCase,
    color: GlobalThemeColors.Blue,
  })
  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Secondary,
    textStyling: GlobalTextStyling.UpperCase,
    color: GlobalThemeColors.Blue,
  })

  const itemStyle = useStyle({
    borderColor: GlobalThemeColors.Gray3ToBlack,
  })

  const COUNT_TRANSLATION = useTranslatedSmartCount(
    [REVIEWS.ACCORDING_TO_SINGLE_REVIEW, REVIEWS.ACCORDING_TO_MULTIPLE_REVIEWS],
    productReviewsCount,
  )

  return (
    <div
      ref={innerRef}
      className={cx(css.ProductReviewsList, className, gridStyle)}>
      <h3 className={cx(css.title, titleStyle)}>{t(REVIEWS.COMMENTS)}</h3>

      <div className={cx(css.content)}>
        {header?.image && (
          <Image
            className={cx(css.image, css.hideOnSmallScreen)}
            layout="fill"
            sizesFromBreakpoints={IMAGE_SIZES}
            objectFit="cover"
            ratio={0.75}
            {...header.image}
          />
        )}

        <div className={cx(css.contentContainer)}>
          {header?.title && <div className={titleStyle}>{header.title}</div>}
          {numberAsBoolean(productAverageRatings) &&
            productReviewsCount > 0 && (
              <div className={cx(css.ratingsRow)}>
                <StarRatings
                  itemClassName={cx(css.star)}
                  ratings={productAverageRatings}
                />
                <div className={cx(css.ratingsText, textStyle)}>
                  {COUNT_TRANSLATION}
                </div>
              </div>
            )}
          {header?.link && (
            <BlurredCta
              className={cx(css.cta)}
              onClick={() => {
                triggerEvent()
              }}
              theme={GlobalThemeColors.Black}
              {...header.link}>
              {t(REVIEWS.LEAVE_REVIEW)}
            </BlurredCta>
          )}
        </div>
      </div>
      {processedResults && processedResults?.length > 0 && (
        <>
          <div className={cx(css.reviews)}>
            {processedResults &&
              processedResults?.map((review, i) => (
                <ProductReview
                  key={`review_${i}`}
                  className={cx(css.reviewItem, itemStyle)}
                  {...review}
                />
              ))}
          </div>
          {processedPage?.current < pagesTotal && (
            <div className={cx(css.ctaContainer)}>
              <BlurredCta
                withBackground
                theme={GlobalThemeColors.Black}
                className={cx(css.cta)}
                isLoading={isLoading}
                isBig
                onClick={() => loadNextPage()}>
                {t(CTA.LOAD_MORE)}
              </BlurredCta>
            </div>
          )}
        </>
      )}
    </div>
  )
}

ProductReviewsList.defaultProps = {}

function ProductReviewsListForwarded(props, ref) {
  return <ProductReviewsList innerRef={ref} {...props} />
}

export default forwardRef<any, ProductReviewsListProps>(
  ProductReviewsListForwarded,
)
